import { Button, Icon, Unifree } from '@yes.technology/react-toolkit'
import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import 'react-medium-image-zoom/dist/styles.css'

import useModal from '../../../../shared/hooks/useModal'
import usePermission from '../../../../shared/hooks/usePermission'
import PermissionDeniedCard from '../../../../shared/PermissionDeniedCard'
import WebcamCapture from '../../../../shared/WebcamCapture'
import useUploadS3, {
  UploadFileResult
} from '../../../hooks/useUploadS3/useUploadS3'
import useDocument from '../../../../shared/hooks/useDocument'
import FileUploader from '../../../../shared/FileUploader'
import ViewPhoto from '../../../../shared/ViewPhoto'
import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import { useDocumentURL } from 'modules/document/hooks'
import useIsOpen from 'modules/shared/hooks/useIsOpen'
import InstructionModal from './InstructionModal'
import { useAppDispatch } from 'state/store'
import { startLoading, stopLoading } from 'modules/interaction/redux'
import useBlueprintsContext from 'modules/interaction/hooks/useBlueprintsContext'

type ActivityDocumentPhotoProps = {
  uuid?: string
  inputValue: string
  required?: boolean
  onChange: (value: string) => void
  label: string
  maxSizeInBytes?: number
  instructionImageUrl?: string
  instructionModal?: {
    title: string
    subtitle: string
    des?: string
    complement?: string
    image_url?: string
  }
}

type Values = {
  uuid?: string
  uuid_status?: string
  statusDes?: string
  contentType?: string
  size?: string
  url?: string
  des?: string
  file?: string
}

const ActivityDocumentPhoto = ({
  uuid,
  inputValue,
  onChange,
  label,
  required,
  maxSizeInBytes,
  instructionModal,
  instructionImageUrl
}: ActivityDocumentPhotoProps) => {
  const { t } = useTranslation()
  const { createOrUpdateDocument } = useDocument()
  const { isOpenModal, openModal, closeModal } = useModal()
  const { permissionDenied, requestAccess, clearPermissionDenied } =
    usePermission()
  const {
    isOpen: isWebcamOpen,
    open: openWebcam,
    close: closeWebcam
  } = useIsOpen()
  const { uploadFile } = useUploadS3()

  const dispatch = useAppDispatch()
  const { executeBlueprints } = useBlueprintsContext()

  const [view, setView] = useState(false)
  const [values, setValues] = useState<Values>()

  useEffect(() => {
    if (inputValue !== '') {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  const startLoadingPage = () => {
    dispatch(
      startLoading({
        des: t('file-uploader.loading.des'),
        complement: t('file-uploader.loading.complement')
      })
    )
  }

  const stopLoadingPage = () => {
    dispatch(stopLoading())
  }

  const onSuccessUploadFile = async ({ url, content }: UploadFileResult) => {
    const values = inputValue && JSON.parse(inputValue)
    const { document, statusDes } = await createOrUpdateDocument({
      uuid: values?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    const newValues = JSON.stringify({
      ...values,
      uuid: document?.uuid,
      uuid_status: statusDes,
      des: document?.des,
      contentType: content.type,
      size: content.size,
      url
    })

    onChange(newValues)
    executeBlueprints({
      type: 'activity_changed',
      activityValue: newValues,
      activityModelUuid: uuid
    })
    stopLoadingPage()
  }

  const capturePhoto = (img: string) => {
    startLoadingPage()

    // close camera
    closeWebcam()

    uploadFile({
      file: img,
      fileType: 'base64'
    })
      .then(onSuccessUploadFile)
      .catch(stopLoadingPage)
  }

  const onChangeDescription = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const saveDescription = (event: FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !documentURL) {
      return
    }

    setTimeout(async () => {
      const values = JSON.parse(inputValue)
      const { document, statusDes, statusUuid } = await createOrUpdateDocument(
        {
          uuid: values.uuid,
          des: event.target.value
        },
        false
      )

      onChange(
        JSON.stringify({
          ...values,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusUuid,
          statusDes: statusDes
        })
      )
    }, 500)
  }

  const uploadLocalFile = (file: File) => {
    startLoadingPage()

    uploadFile({
      file
    })
      .then(onSuccessUploadFile)
      .catch(stopLoadingPage)
  }

  const clearData = () => {
    onChange('')
    setValues({})
    executeBlueprints({
      type: 'activity_changed',
      activityValue: '',
      activityModelUuid: uuid
    })
  }

  const onContinue = () => {
    closeModal()
    openWebcam()
  }

  const handleCameraClick = async () => {
    const granted = await requestAccess()

    if (!granted) return

    if (instructionModal) {
      openModal()
    } else {
      openWebcam()
    }
  }

  const { documentURL } = useDocumentURL({ s3PrivateURL: values?.url })

  return (
    <>
      {!isWebcamOpen && (
        <div style={{ display: 'grid', rowGap: '8px' }}>
          <Unifree
            label={t('activity-document-photo.labels.des')}
            value={values?.des || ''}
            onChange={onChangeDescription}
            onBlur={saveDescription}
            data-testid='description-field'
            disabled={!documentURL}
            placeholder={t('activity-document-photo.placeholders.des')}
          />
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1'>
              <Unifree
                label={label || t('activity-document-photo.labels.uuid-foto')}
                disabled={true}
                value={values?.uuid || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.uuid-foto'
                )}
              />
            </div>
            {documentURL ? (
              <Button
                onClick={() => setView(true)}
                variant='secondary'
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Visualization' icSize='semiSmall' />
              </Button>
            ) : (
              <Button
                onClick={handleCameraClick}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='TakePhoto' icSize='semiSmall' />
              </Button>
            )}
            {!documentURL && (
              <FileUploader
                onChange={uploadLocalFile}
                accept='image/*'
                variant='secondary'
                maxSizeInBytes={maxSizeInBytes}
              />
            )}
            {documentURL && (
              <Button
                variant='secondary'
                onClick={clearData}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Cleaner' icSize='semiSmall' />
              </Button>
            )}
          </div>
          <Unifree
            label={t('activity-document-photo.labels.status')}
            disabled={true}
            value={values?.statusDes || ''}
            required={required}
          />
          <div className='row'>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.content-type')}
                disabled={true}
                value={values?.contentType || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.content-type'
                )}
              />
            </div>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.size')}
                disabled={true}
                value={values?.size ? formatFileSize(Number(values.size)) : ''}
                required={required}
                placeholder={t('activity-document-photo.placeholders.size')}
              />
            </div>
          </div>

          <div className='w-100 d-flex justify-content-center align-items-center'>
            {documentURL ? (
              <img
                src={documentURL}
                className='img-fluid'
                alt={`${values?.des || 'Photo'}`}
              />
            ) : (
              <div className='w-100 webcam-no-image rounded'>
                {t('activity-document-photo.no-image')}
              </div>
            )}
          </div>
        </div>
      )}
      {isWebcamOpen && (
        <WebcamCapture
          onSuccess={capturePhoto}
          onCancel={closeWebcam}
          overlayImage={instructionImageUrl}
        />
      )}
      {instructionModal && (
        <InstructionModal
          isOpen={isOpenModal}
          onContinue={onContinue}
          onCancel={closeModal}
          title={instructionModal.title}
          subtitle={instructionModal.subtitle}
          des={instructionModal.des}
          complement={instructionModal.complement}
          imageUrl={instructionModal.image_url}
        />
      )}

      {view && documentURL && (
        <ViewPhoto onCancel={() => setView(false)} image={documentURL} />
      )}
      <PermissionDeniedCard
        isOpen={permissionDenied}
        onClose={clearPermissionDenied}
        title={t('permission.denied.camera-title')}
      />
    </>
  )
}

export default ActivityDocumentPhoto
